'use client'

/**
 * BreadcrumbsJsonLd — Auto-generierte BreadcrumbList aus dem aktuellen Pathname.
 *
 * Rendert Schema.org BreadcrumbList — Suchmaschinen zeigen damit in den
 * Ergebnissen "FGL > Wertung > Detail" statt nur die volle URL. Auf
 * Home-Seite ("/") NICHT ausgegeben.
 *
 * r19.55.203 (2026-08-11): GEO-Infrastruktur-Sprint.
 */

import { usePathname } from 'next/navigation'
import { useTranslation } from '@/i18n/client'

const ORIGIN = 'https://free-glide-league.com'

/** Human-lesbare Segment-Labels — deutschsprachig, entsprechend NAV-Menue */
const SEGMENT_LABELS: Record<string, string> = {
  wertung: 'Wertung',
  features: 'Features',
  faq: 'FAQ',
  news: 'News',
  podcast: 'Podcast',
  weather: 'Wetter',
  'flying-fields': 'Fluggebiete',
  'airspace-3d': '3D-Luftraum',
  gallery: 'Galerie',
  mitmachen: 'Mitmachen',
  contacts: 'Ansprechpartner',
  nutzungsbedingungen: 'Nutzungsbedingungen',
  wettbewerbsordnung: 'Wettbewerbsordnung',
  datenschutz: 'Datenschutz',
  impressum: 'Impressum',
  login: 'Anmelden',
  register: 'Registrieren',
  donate: 'Förderbeitrag',
  pilot: 'Pilot',
  flight: 'Flug',
  club: 'Verein',
  'aircraft-models': 'FGL-Modelle',
  downloads: 'Downloads',
  sync: 'Sync',
  battery: 'Akku',
  terrain: 'Terrain',
  'pilotenboerse-regeln': 'Pilotenbörse-Regeln',
  presse: 'Presse',
}

function labelFor(seg: string): string {
  if (SEGMENT_LABELS[seg]) return SEGMENT_LABELS[seg]
  // Dynamische Segmente (Slugs/IDs) — first-Letter-Upper, kein Decoding
  return decodeURIComponent(seg)
    .replace(/-/g, ' ')
    .replace(/\b\w/g, (m) => m.toUpperCase())
}

export function BreadcrumbsJsonLd() {
  const pathname = usePathname()
  const { t } = useTranslation()
  if (!pathname || pathname === '/') return null
  // /app + /admin sind private Bereiche — nicht in Breadcrumbs
  if (pathname.startsWith('/app') || pathname.startsWith('/admin')) return null

  const parts = pathname.split('/').filter(Boolean)
  if (parts.length === 0) return null

  const items = [
    {
      '@type': 'ListItem',
      position: 1,
      name: t('nav.home') || 'Startseite',
      item: `${ORIGIN}/`,
    },
    ...parts.map((seg, i) => ({
      '@type': 'ListItem',
      position: i + 2,
      name: labelFor(seg),
      item: `${ORIGIN}/${parts.slice(0, i + 1).join('/')}`,
    })),
  ]

  const jsonLd = {
    '@context': 'https://schema.org',
    '@type': 'BreadcrumbList',
    itemListElement: items,
  }

  return (
    <script
      type="application/ld+json"
      dangerouslySetInnerHTML={{ __html: JSON.stringify(jsonLd) }}
    />
  )
}
